<template>
  <div class="flex border-b-1 border-b-[#F4F4F4] mb-17px" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
    <div class="pr-12px" :alt="message.senderName">
      <img
        v-if="message.type === '3'"
        :src="getUrl(message.senderAvatar)"
        alt="avatar"
        :title="message.senderName || '未知用户'"
        class="w-32px h-32px rounded-full bg-[#f6f7f9]"
        @error="loadAvatarError"
      />
      <div
        v-else-if="message.type === '4'"
        class="w-32px h-32px rounded-full bg-[#f6f7f9] flex justify-center items-center"
      >
        <cat-icon name="icon_icon_notice" prefix="iconfont_menu" />
      </div>
    </div>
    <div class="flex-1 mt-5px text-sm cursor-pointer">
      <div class="flex flex-col" :class="{ 'gray-txt': readedInUnreadList }" @click="markRead">
        <div class="flex justify-between">
          <div>
            <span class="font-semibold text-neutral-800">{{ message.typeName || '-' }}</span>
            <span v-if="message.type === '3'" class="pl-10px">
              <cat-state class="inline-block" :text="getStatus(statusCode).label" :color="getStatus(statusCode).color">
              </cat-state>
            </span>
          </div>
          <div class="text-secondary">
            <div v-if="message.readStatus === '0'">
              <div v-if="selectedMessage">
                <cat-icon name="common_icon_tick" class="hover:text-blue-500 text-lg" prefix="iconfont"></cat-icon>
              </div>
              <div v-else>{{ getDayDiff(message.createTime) }}</div>
            </div>
            <div v-else>
              {{ getDayDiff(message.createTime) }}
            </div>
          </div>
        </div>
        <div class="mt-4px mr-62px text-neutral-800">
          <div>
            {{ message.applicantName ? `${message.applicantName}&nbsp;&nbsp;&nbsp;&nbsp;` : '' }}
            {{ message.title }}
          </div>
        </div>
        <div class="mt-4px mr-62px mb-17px text-[var(--color-text-secondary)] leading-[22px] relative">
          <span ref="measuringContainer" class="hidden whitespace-pre inline-block align-top">
            {{ message.content }}
          </span>

          <!-- 显示内容 -->
          <span v-if="showMore">{{ message.content }}</span>
          <span v-else>{{ truncatedContent }}</span>

          <!-- 展开按钮 -->
          <a
            v-if="needsExpand && !showMore"
            class="text-primary cursor-pointer ml-1 absolute right-[0] translate-x-[100%]"
            @click.stop="showMore = true"
          >
            展开全部
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import dayjs from 'dayjs';
import { truncate } from 'lodash-es';
import { ISysMessage } from '@/api/system/sysMessage';
import IMG_AVATAR from '@/assets/images/avatar.png';

const props = defineProps({
  message: {
    type: Object as PropType<ISysMessage>,
    required: true,
  },
  activeTabName: {
    type: String,
    required: true,
  },
});

const { message: currentMessage, activeTabName } = toRefs(props);

const showMore = ref<boolean>(false);
const selectedMessage = ref<boolean>(false);

/** 计算时间差 */
function getDayDiff(time: Date) {
  if (!time) return '-';
  const minuteDiff = dayjs().diff(dayjs(time), 'minutes');
  if (minuteDiff < 1) {
    return '刚刚';
  } else if (minuteDiff < 60) {
    return minuteDiff + '分钟前';
  }
  const hourdiff = dayjs().diff(dayjs(time), 'hours');
  if (hourdiff < 24) {
    return hourdiff + '小时前';
  }
  const daydiff = dayjs().diff(dayjs(time), 'days');
  if (daydiff > 0 && daydiff < 30) {
    return daydiff + '天前';
  }
  const monthdiff = dayjs().diff(dayjs(time), 'months');
  const sameYear = dayjs().year() === dayjs(time).year();
  if (monthdiff > 0 && monthdiff < 3) {
    return monthdiff + '月前';
  } else if (monthdiff >= 3 && monthdiff < 12 && sameYear) {
    return dayjs(time).format('MM-DD');
  }
  return dayjs(time).format('YYYY-MM-DD');
}

function getDataLength(message: string) {
  if (!message) return 0;
  return message.length;
}
/** 截取消息 */
function truncateMessage(message: string, length = 35) {
  if (!message) return '-';
  return truncate(message, { length, omission: '...' });
}

const { ROSA_MINIO_FILE } = useAppStore();
const getUrl = (url: string) => {
  return `${ROSA_MINIO_FILE}${url}`;
};
function loadAvatarError(e: any) {
  e.target.src = IMG_AVATAR;
  e.target.onerror = null;
}

const statusCode = computed(() => {
  const idx = props.message.title.indexOf('：');
  const str = props.message.title.substring(0, idx);
  if (str === '待审批通知') return '0';
  else return '1';
});

function getStatus(statusCode: string) {
  switch (statusCode) {
    case '0':
      return {
        // label: '审核未通过',
        // color: '#ff6565',
        label: '待审核',
        color: 'var(--color-warning)',
      };
    case '1':
      return {
        label: '审核通过',
        color: '#3dca97',
      };
    default:
      return {
        label: '',
        color: '',
      };
  }
}

function handleMouseEnter() {
  selectedMessage.value = true;
}
function handleMouseLeave() {
  selectedMessage.value = false;
}

const readedInUnreadList = computed(() => {
  return currentMessage.value.readStatus === '1' && activeTabName.value === 'unRead';
});

async function markRead() {
  if (
    currentMessage.value.readStatus === '0' &&
    !(getDataLength(currentMessage.value.content) > 60 && !showMore.value)
  ) {
    await readOne(currentMessage.value.id);
    currentMessage.value.readStatus = '1';
  }
  showMore.value = true;
}

// 响应式变量
const measuringContainer = ref<HTMLElement | null>(null);
const truncatedContent = ref<string>('');
const needsExpand = ref<boolean>(false);

// 测量文本宽度
function measureTextWidth(text: string): number {
  if (!measuringContainer.value) return 0;

  const style = window.getComputedStyle(measuringContainer.value);

  const font = `${style.fontSize} ${style.fontFamily}`;

  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  ctx.font = font;
  // console.log(font, 'font');

  return ctx.measureText(text).width;
}

// 更新截断内容
function updateTruncatedContent() {
  if (!measuringContainer.value) return;

  const container = measuringContainer.value.parentElement;
  if (!container) return;

  const availableWidth = container.clientWidth;
  const fullText = currentMessage.value.content;
  const fullTextWidth = measureTextWidth(fullText);

  if (fullTextWidth <= availableWidth) {
    truncatedContent.value = fullText;
    needsExpand.value = false;
  } else {
    let low = 0;
    let high = fullText.length;
    let result = '';

    while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      const testText = fullText.slice(0, mid) + '...';
      const testWidth = measureTextWidth(testText);

      if (testWidth <= availableWidth) {
        result = testText;
        low = mid + 1;
      } else {
        high = mid - 1;
      }
    }

    truncatedContent.value = result;
    needsExpand.value = true;
  }
}

// 监听 mounted 和 resize
onMounted(() => {
  updateTruncatedContent();
  window.addEventListener('resize', updateTruncatedContent);
});

// 当内容变化时重新计算
watch(
  () => currentMessage.value.content,
  () => {
    updateTruncatedContent();
  },
);

// 清理监听器
onBeforeUnmount(() => {
  window.removeEventListener('resize', updateTruncatedContent);
});

onMounted(() => {});
const { readOne } = useSysMessageStore();
</script>

<style lang="scss" scoped>
.gray-txt {
  div {
    color: #999;
  }
}
</style>
